import { CheckCircle2, Loader2, Circle, AlertTriangle } from "lucide-react";

/**
 * 6-stage Intake Engine status panel — visualises the multi-engine pipeline.
 *
 * Stages (in order):
 *   1. Upload received
 *   2. File parsed
 *   3. Document type identified
 *   4. Candidate records extracted
 *   5. Records normalized
 *   6. Ready for AI diagnostic
 *
 * Each stage's status is derived from the live intake response, so the user
 * can see exactly where the pipeline is.
 */

const STAGE_DEFS = [
  { key: "upload_received",        label: "Upload received",          copy: "Files received successfully" },
  { key: "file_parsed",            label: "File parsed",              copy: "Uploaded files were parsed into structured or semi-structured content" },
  { key: "document_type",          label: "Document type identified", copy: "Each file was classified as bank statement, invoice, credit note, expense, receipt, bill, or unknown" },
  { key: "records_extracted",      label: "Candidate records extracted", copy: "Potential financial records were extracted from uploaded files" },
  { key: "records_normalized",     label: "Records normalized",       copy: "Extracted records were converted into the canonical financial format" },
  { key: "ready_for_diagnostic",   label: "Ready for AI diagnostic",  copy: "Normalized records are ready for financial diagnosis" },
];

function stageStatus(key, intake, fileResults) {
  // intake: {intake_state, files_attempted, files_succeeded, files_failed, records_extracted, records_normalized, records_flagged_for_review}
  const is = intake?.intakeState;
  if (!is || is === "idle") return "pending";
  if (is === "uploading" && key === "upload_received") return "running";
  if (is === "uploading") return "pending";

  // Processing state — sequential stages light up as we infer completeness
  switch (key) {
    case "upload_received":
      return intake.filesAttempted > 0 ? "completed" : "pending";
    case "file_parsed": {
      if (is === "processing") return intake.filesSucceeded > 0 ? "completed" : "running";
      if (intake.filesSucceeded === 0) return "failed";
      return intake.filesFailed > 0 ? "completed_with_issues" : "completed";
    }
    case "document_type": {
      if (is === "processing") return intake.filesSucceeded > 0 ? "completed" : "pending";
      const anyClassified = (fileResults || []).some(f => f.document_type && f.document_type !== "unknown");
      if (intake.filesSucceeded === 0) return "failed";
      return anyClassified ? "completed" : "completed_with_issues";
    }
    case "records_extracted":
      if (is === "processing") return intake.recordsExtracted > 0 ? "completed" : "running";
      return intake.recordsExtracted > 0 ? "completed" : "failed";
    case "records_normalized":
      if (is === "processing") return intake.recordsNormalized > 0 ? "completed" : "running";
      if (intake.recordsNormalized === 0) return "failed";
      return intake.recordsNormalized < intake.recordsExtracted ? "completed_with_issues" : "completed";
    case "ready_for_diagnostic":
      if (is === "processing") return "pending";
      if (is === "failed" || intake.recordsNormalized === 0) return "failed";
      return is === "completed_with_issues" ? "completed_with_issues" : "completed";
    default:
      return "pending";
  }
}

function StageIcon({ status }) {
  if (status === "running") return <Loader2 className="w-4 h-4 text-cyan-400 animate-spin shrink-0" />;
  if (status === "completed") return <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />;
  if (status === "completed_with_issues") return <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />;
  if (status === "failed") return <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />;
  return <Circle className="w-4 h-4 text-slate-700 shrink-0" />;
}

/**
 * @param {{
 *   intakeState: string,
 *   filesAttempted: number,
 *   filesSucceeded: number,
 *   filesFailed: number,
 *   recordsExtracted: number,
 *   recordsNormalized: number,
 *   recordsFlaggedForReview: number,
 *   fileResults: any[],
 * }} props
 */
export default function IntakeStagesPanel({
  intakeState, filesAttempted, filesSucceeded, filesFailed,
  recordsExtracted, recordsNormalized, recordsFlaggedForReview,
  fileResults = [],
}) {
  if (!intakeState || intakeState === "idle") return null;
  const intake = {
    intakeState, filesAttempted, filesSucceeded, filesFailed,
    recordsExtracted, recordsNormalized, recordsFlaggedForReview,
  };
  return (
    <div className="rounded-xl border border-slate-700/30 bg-slate-900/30 p-4" data-testid="intake-stages-panel">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[10px] text-slate-500 uppercase tracking-[0.15em] font-semibold">Document Intake Engine · pipeline</span>
      </div>
      <div className="space-y-2">
        {STAGE_DEFS.map((stage, idx) => {
          const status = stageStatus(stage.key, intake, fileResults);
          return (
            <div key={stage.key} className="flex items-start gap-3" data-testid={`intake-stage-${stage.key}`} data-stage-status={status}>
              <span className="text-[10px] text-slate-600 font-mono pt-0.5 shrink-0 w-4">{idx + 1}</span>
              <StageIcon status={status} />
              <div className="flex-1 min-w-0">
                <div className={`text-xs font-semibold ${
                  status === "completed" ? "text-emerald-300" :
                  status === "completed_with_issues" ? "text-amber-300" :
                  status === "failed" ? "text-red-300" :
                  status === "running" ? "text-cyan-300" :
                  "text-slate-500"
                }`}>{stage.label}</div>
                <div className="text-[10px] text-slate-500 leading-snug">{stage.copy}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
